// @flow
import classnames from 'classnames';
import { memoize } from 'lodash';
import React, { Children, Component, type Node } from 'react';

import * as featureTesting from 'Components/utils/feature-testing';
import { ArrowButton } from 'Components/frameworks/arrow-button/ArrowButton';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';

import { themes } from './Scrollable.themes';

const WHEEL_DELTA_SCROLL_FACTOR = 50;
const CLICK_DELTA_SCROLL_FACTOR = 200;
const MOUSE_DELTA_SCROLL_FACTOR = 50;

type Props = {
  backwardLabel?: string,
  children: Node | null,
  childrenWrapperClassNames?: string,
  className?: string,
  deltaScrollFactorClick?: number,
  deltaScrollFactorMouse?: number,
  deltaScrollFactorWheel?: number,
  elementWidth?: number,
  fadeOut?: boolean,
  forwardLabel?: string,
  hasNoMouseWheelScroll?: boolean,
  hasShadow?: boolean,
  initialPosition?: number | null,
  header?: Node | null,
  navPosition?: 'bottom' | 'center' | 'side' | 'top',
  theme?: string,
};

type State = {
  hasScrollLeft: boolean,
  hasScrollRight: boolean,
};

export class Scrollable extends Component<Props, State> {
  scrollAreaNode: ?HTMLElement;
  scrolling: *;
  static contextType = ResponsiveContext;

  static defaultProps = {
    children: null,
    deltaScrollFactorClick: CLICK_DELTA_SCROLL_FACTOR,
    deltaScrollFactorMouse: MOUSE_DELTA_SCROLL_FACTOR,
    deltaScrollFactorWheel: WHEEL_DELTA_SCROLL_FACTOR,
    elementWidth: 82,
    fadeOut: false,
    hasNoMouseWheelScroll: false,
    hasShadow: false,
    initialPosition: null,
    navPosition: 'center',
    theme: 'default',
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      hasScrollLeft: false,
      hasScrollRight: false,
    };
  }

  componentDidMount() {
    if (!this.scrollAreaNode) {
      return;
    }

    if (this.props.hasNoMouseWheelScroll) {
      this.onScroll();
      return;
    }
    if (typeof window !== 'undefined') {
      // $FlowFixMe
      this.scrollAreaNode.addEventListener('scroll', this.onScroll, false);
      // $FlowFixMe
      this.scrollAreaNode.addEventListener('mousewheel', this.onMouseWheel, false);
      // $FlowFixMe
      this.scrollAreaNode.addEventListener('DOMMouseScroll', this.onMouseWheel, false); // Firefox
      this.onScroll();
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (
      prevProps.initialPosition === null &&
      prevProps.initialPosition !== this.props.initialPosition
    ) {
      this.initScrollPosition();
    }

    const { hasScrollLeft, hasScrollRight } = this.processScroll();

    if (prevState.hasScrollRight !== hasScrollRight || prevState.hasScrollLeft !== hasScrollLeft) {
      this.setState({
        hasScrollLeft,
        hasScrollRight,
      });
    }
  }

  componentWillUnmount() {
    if (!this.scrollAreaNode || this.props.hasNoMouseWheelScroll) {
      return;
    }
    if (typeof window !== 'undefined') {
      // $FlowFixMe
      this.scrollAreaNode.removeEventListener('scroll', this.onScroll, false);
      // $FlowFixMe
      this.scrollAreaNode.removeEventListener('mousewheel', this.onMouseWheel, false);
      // $FlowFixMe
      this.scrollAreaNode.removeEventListener('DOMMouseScroll', this.onMouseWheel, false);
    }
  }

  /**
   * Sometine IE 11 cannot get the right scrollWidth
   * It returns scrollWidth + 1
   */
  shouldFixBrowser(isIE11: boolean) {
    if (isIE11) {
      // $FlowFixMe
      const { clientWidth, scrollWidth } = this.scrollAreaNode;
      // $FlowFixMe
      const boundingClientRectWidth = Math.round(this.scrollAreaNode.getBoundingClientRect().width);

      const diff = scrollWidth - clientWidth;

      return scrollWidth > clientWidth && boundingClientRectWidth === clientWidth && diff === 1;
    }
    return false;
  }

  initScrollPosition() {
    if (!this.scrollAreaNode) {
      return;
    }

    if (this.props.elementWidth && this.props.initialPosition) {
      const normalizedPosition =
        !this.isIE11() && this.context.isRTL
          ? // $FlowFixMe
            -1 * this.props.initialPosition
          : this.props.initialPosition;
      // $FlowFixMe
      this.scrollAreaNode.scrollLeft = this.props.elementWidth * normalizedPosition;
    }
  }

  processScroll() {
    if (!this.scrollAreaNode) {
      return {
        hasScrollLeft: false,
        hasScrollRight: false,
      };
    }

    const { scrollLeft, clientWidth, scrollWidth } = this.scrollAreaNode;
    // $FlowFixMe
    const isRTL = document.documentElement.dir === 'rtl';

    const isIE11 = this.isIE11();
    const browserFix = this.shouldFixBrowser(isIE11) ? 1 : 0;

    const hasScrollRight =
      isRTL && !isIE11 ? scrollLeft < 0 : scrollLeft + clientWidth < scrollWidth - browserFix;
    // the minus 1 is here to handle scroll correctly, I don't know where it came this pixel
    const hasScrollLeft =
      isRTL && !isIE11 ? scrollLeft > -(scrollWidth - clientWidth - 1) : scrollLeft > 0;

    return {
      hasScrollLeft: isIE11 && isRTL ? hasScrollRight : hasScrollLeft,
      hasScrollRight: isIE11 && isRTL ? hasScrollLeft : hasScrollRight,
    };
  }

  onMouseWheel = (e: Event) => {
    if (!this.scrollAreaNode) {
      return;
    }
    // $FlowFixMe
    const wheelDelta = Math.max(-1, Math.min(1, e.wheelDelta || -e.detail));
    // $FlowFixMe
    this.scrollAreaNode.scrollLeft -= wheelDelta * this.props.deltaScrollFactorWheel;

    // Prevent page to scroll down
    if (this.state.hasScrollLeft || this.state.hasScrollRight) {
      e.preventDefault();
    }
  };

  onScroll = () => {
    const state = this.processScroll();
    this.setState(state);
  };

  isIE11 = memoize(() => {
    return !!window.MSInputMethodContext && !!document.documentMode;
  });

  getNormalizedDirection(direction: number) {
    return this.isIE11() && this.context.isRTL ? -1 * direction : direction;
  }

  onMouseDown = (direction: number) => () => {
    if (!this.scrollAreaNode) {
      return;
    }

    this.scrolling = setInterval(() => {
      if (!this.scrollAreaNode) {
        return;
      }
      // $FlowFixMe
      this.scrollAreaNode.scrollLeft +=
        // $FlowFixMe
        this.props.deltaScrollFactorMouse * this.getNormalizedDirection(direction);
      this.onScroll();

      if (
        (direction === -1 && !this.state.hasScrollLeft) ||
        (direction === 1 && !this.state.hasScrollRight)
      ) {
        clearInterval(this.scrolling);
      }
    }, 100);
  };

  onMouseUp = () => {
    clearInterval(this.scrolling);
  };

  onClick = (direction: number) => () => {
    if (!this.scrollAreaNode) {
      return;
    }

    // $FlowFixMe
    this.scrollAreaNode.scrollLeft +=
      // $FlowFixMe
      this.props.deltaScrollFactorClick * this.getNormalizedDirection(direction);

    this.onScroll();
  };

  setScrollAreaRef = (ref: ?HTMLElement) => {
    this.scrollAreaNode = ref;
  };

  shouldDisplayControls(): boolean {
    const { hasScrollLeft, hasScrollRight } = this.state;

    return !featureTesting.hasTouch() && (hasScrollLeft || hasScrollRight);
  }

  render() {
    const {
      backwardLabel,
      children,
      childrenWrapperClassNames,
      className,
      fadeOut,
      forwardLabel,
      hasShadow,
      header,
      navPosition,
      theme,
    } = this.props;

    if (!Children.count(children)) {
      return null;
    }

    const { hasScrollLeft, hasScrollRight } = this.state;
    const { isRTL } = this.context;

    const isNavPositionCenter = navPosition === 'center';

    const shouldFadeOut = fadeOut && isNavPositionCenter;

    const {
      thControl,
      thControlColor,
      thControlColorBg,
      thControlDisabled,
      thControlLeft,
      thControlRight,
      thControls,
      thControlsWrapper,
      thInner,
      thRoot,
    } = themes[theme]({ navPosition, shouldFadeOut });

    const hasControls = this.shouldDisplayControls();

    const rightScrollBtn = (
      <ArrowButton
        className={classnames(thControl, thControlRight, {
          [thControlDisabled]: !hasScrollRight,
        })}
        color={thControlColor}
        colorBg={thControlColorBg}
        hasNoRtl
        isDisabled={!hasScrollRight}
        label={forwardLabel}
        onClick={this.onClick(1)}
        onMouseDown={this.onMouseDown(1)}
        onMouseUp={this.onMouseUp}
        rotation={180}
      />
    );

    const leftScrollBtn = (
      <ArrowButton
        className={classnames(thControl, thControlLeft, {
          [thControlDisabled]: !hasScrollLeft,
        })}
        color={thControlColor}
        colorBg={thControlColorBg}
        hasNoRtl
        isDisabled={!hasScrollLeft}
        label={backwardLabel}
        onClick={this.onClick(-1)}
        onMouseDown={this.onMouseDown(-1)}
        onMouseUp={this.onMouseUp}
      />
    );

    return (
      <div
        className={classnames(className, thRoot)}
        dir={isRTL ? 'rtl' : 'ltr'}
        data-testid="scrollable"
      >
        {(hasControls || !!header) &&
          isRTL &&
          !isNavPositionCenter && (
            <div className={thControlsWrapper}>
              {header}
              {hasControls && (
                <div className={thControls} data-testid="Scrollable-controls">
                  {rightScrollBtn}
                  {leftScrollBtn}
                </div>
              )}
            </div>
          )}
        <div
          className={classnames('overflow-hidden relative', thInner, {
            'shadow-inner-left': hasShadow && hasScrollLeft,
            'shadow-inner-right': hasShadow && hasScrollRight,
          })}
        >
          <div
            className={classnames(childrenWrapperClassNames, 'scroll-area')}
            ref={this.setScrollAreaRef}
          >
            {children}
            {shouldFadeOut ? (
              <React.Fragment>
                <div className="absolute rtl:right-0 top-0 bottom-0 rtl:scrollableOverflowFadeOut-gradient scrollableOverflowFadeOut-width" />
                <div className="mx-1 h-px rtl:-order-1 flex-shrink-0 scrollableOverflowFadeOut-width" />
              </React.Fragment>
            ) : null}
          </div>
        </div>
        {(hasControls || !!header) &&
          !isRTL && (
            <div className={thControlsWrapper}>
              {header}
              {hasControls && (
                <div className={thControls} data-testid="Scrollable-controls">
                  {leftScrollBtn}
                  {rightScrollBtn}
                </div>
              )}
            </div>
          )}
        {hasControls &&
          isRTL &&
          isNavPositionCenter && (
            <div className={thControls} data-testid="Scrollable-controls">
              {rightScrollBtn}
              {leftScrollBtn}
            </div>
          )}
      </div>
    );
  }
}
